import { useState, useEffect, useRef } from 'react'

type UseCountUpProps = {
  end: number
  duration?: number
  start?: number
}

export const useCountUp = ({
  end,
  duration = 2000,
  start = 0,
}: UseCountUpProps): number => {
  const [count, setCount] = useState(start)
  const startTimeRef = useRef<number | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

  const animateCount = (timestamp: number) => {
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp
    }

    const progress = timestamp - startTimeRef.current
    const percentage = Math.min(progress / duration, 1)
    const easedPercentage = easeOutCubic(percentage)
    const currentCount = start + (end - start) * easedPercentage

    setCount(Math.floor(currentCount))

    if (percentage < 1) {
      animationFrameRef.current = requestAnimationFrame(animateCount)
    }
  }

  useEffect(() => {
    animationFrameRef.current = requestAnimationFrame(animateCount)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      startTimeRef.current = null
    }
  }, [end, duration, start])

  return count
}
